import { get, orderBy } from 'lodash';
import { createSelector } from 'reselect';
import { RootState } from '../../../store';
import { includes, startsWith, toLowerCase } from '../../../utils';
import { getProducts } from '../../product/redux/productSelectors';
import { getStocks } from '../../stock/redux/stockSelectors';
import { getStockAmount } from '../../stock/stockFunctions';
import { ProductStock } from '../../stock/stockTypes';
import { InactivatePriceRow, InactivatePriceColumn } from '../inactivatePriceTypes';

export const getInactivatePricesTableIsLoading = (state: RootState) => state.inactivatePrices.ui.isLoading;
export const getInactivatePricesTableValidFroms = (state: RootState) =>
    state.inactivatePrices.prices.availablePricelistValidFromDates;
export const getInactivatePricesTableSelectedPricelistIds = (state: RootState) =>
    state.inactivatePrices.prices.selectedPricelistIds;

export const getInactivatePricesTableSelectedPricelistDate = (state: RootState) =>
    state.inactivatePrices.prices.selectedPricelistDate;
export const getInactivatePricesTable = (state: RootState) => state.inactivatePrices.ui.table;

export const getInactivatePrices = (state: RootState) => state.inactivatePrices.prices.prices;
export const getInactivatePricesTableSelectedWarehouse = (state: RootState) =>
    state.inactivatePrices.prices.selectedWarehouse;

export const getInactivatePricesCheckboxFilters = (state: RootState) =>
    state.inactivatePrices.ui.checkboxFilters;

export const getInactivatePriceRows = createSelector(
    [
        getProducts,
        getInactivatePrices,
        getInactivatePricesTableSelectedPricelistIds,
        getStocks,
        getInactivatePricesTableSelectedWarehouse,
        getInactivatePricesTable,
        getInactivatePricesCheckboxFilters
    ],
    (
        products,
        prices,
        selectedPricelistIds,
        stocks,
        selectedWarehouse,
        { sorting, filters },
        checkboxFilters
    ) => {
        let productStocks: ProductStock = {};
        if (selectedWarehouse.id != null) {
            productStocks = stocks[selectedWarehouse.id] ?? {};
        }

        function getStock(productId: number) {
            const productStockGrp = productStocks[productId];
            if (productStockGrp == null) {
                return null;
            }
            return getStockAmount(selectedWarehouse.date, productStockGrp);
        }

        const rows = Object.values(products)
            .map(p => [p, getStock(p.productId)] as const)
            .filter(
                ([p, stock]) =>
                    (selectedWarehouse.id == null || p.warehouseIds?.includes(selectedWarehouse.id ?? 0)) &&
                    selectedPricelistIds.length > 0 &&
                    startsWith(filters.productId, p.productId.toString()) &&
                    includes(filters.productName, p.name) &&
                    includes(filters.unit, p.priceUnit) &&
                    includes(filters.purchaser, p.purchaser) &&
                    includes(filters.country, p.countryOfOrigin) &&
                    startsWith(filters.netWeight, p.netWeight.toString()) &&
                    startsWith(filters.stock, stock?.toString())
            )
            .map(([product, stock]) => {
                const row: InactivatePriceRow = {
                    productId: product.productId,
                    productName: product.name,
                    country: product.countryOfOrigin ?? '',
                    netWeight: product.netWeight,
                    purchaser: product.purchaser ?? '',
                    unit: product.priceUnit ?? '',
                    stock: stock ?? null,
                    agreementPriceIds: []
                };

                for (const pricelistId of selectedPricelistIds) {
                    const price = prices[`${product.productId}-${pricelistId}`];
                    if (price?.priceId != null) {
                        row[pricelistId] = {
                            isActive: price.isActiveToBeSaved ?? price.isActive,
                            isSaving: price.isSaving === true
                        };

                        row.agreementPriceIds.push(...price.agreementPriceIds);
                    }
                }

                return row;
            })
            .filter(priceRow => {
                if (checkboxFilters.showAllArticles) {
                    return true;
                }

                const isActives = Object.entries(priceRow)
                    .filter(([key]) => !isNaN(Number(key)))
                    .map(([, val]) => val as InactivatePriceColumn);

                if (checkboxFilters.showInactiveArticlesOnly) {
                    return isActives.some(x => x != null && x.isActive === false);
                }

                return isActives.some(x => x != null);
            });

        if (!isNaN(Number(sorting.column))) {
            return orderBy(rows, [o => toLowerCase(get(o, sorting.column))], sorting.direction);
        }

        const nullOrderPlacement = sorting.direction === 'asc' ? Infinity : -Infinity;

        return orderBy(
            rows,
            [o => toLowerCase(get(o, sorting.column)) || nullOrderPlacement],
            sorting.direction
        );
    }
);
